import type {
  BuilderCampaignSnapshot,
  ChannelConfig,
  LastActiveStepConfig,
  StepCompletion,
} from "campaign-builder/config/channelConfig";
import { registerChannelConfig } from "campaign-builder/config/channelRegistry";
import { getFormatsFor } from "campaign-builder/formats";
import { validateCampaignName } from "campaign-builder/state";
import { H2hTargetingStep } from "campaign-builder/channels/flyering/targeting";

export interface H2hBuilderSnapshot extends BuilderCampaignSnapshot {
  flyersAmount?: number;
  hasDesign?: boolean;
  trackingValid?: boolean;
}

// Targeting gates on a valid campaign name (the only F2 field for H2H). City + pin
// map gating is added by F3/F4. Design gates on a selected design (F10); Tracking
// gates on the static-only form's validity (F11), reported up from the step.
const getH2hStepCompletion = (campaign: BuilderCampaignSnapshot): StepCompletion => ({
  targeting: validateCampaignName(campaign.campaignName ?? "") === null,
  design: !!(campaign as H2hBuilderSnapshot).hasDesign,
  tracking: !!(campaign as H2hBuilderSnapshot).trackingValid,
});

// Legacy H2H/D2D cursor vocab the platform campaign cards already understand
// (home → audience → design → details → submit). The redesigned 3 steps map onto
// it: targeting ≈ "audience" (city/areas/launch date), design ≈ "design", tracking
// ≈ "details" (the final pre-submit config step). "submit" is a terminal value
// written by the submit flow, so it's omitted from byCompletedStep here.
export const flyeringLastActiveStep: LastActiveStepConfig = {
  initial: "home",
  byCompletedStep: { targeting: "audience", design: "design", tracking: "details" },
  terminalValues: ["submit"],
};

export const h2hChannelConfig: ChannelConfig = {
  channel: "H2H",
  // The builder title/summary use the campaign-type name ("Hand-to-hand"),
  // matching the design + sidebar sub-item; "Flyering" is the parent channel
  // category, not the campaign label.
  label: "Hand-to-hand",
  TargetingStep: H2hTargetingStep,
  summaryUnits: {
    label: "Flyers",
    getValue: (campaign) => (campaign as H2hBuilderSnapshot).flyersAmount ?? 0,
  },
  getFormats: (countryCode) => getFormatsFor("H2H", countryCode),
  design: {
    addressBoxRequired: false,
    maxFileSizeMb: 50,
    acceptedFileTypes: ["application/pdf"],
    showFormatSelector: true,
    formatDisclaimerChannel: "Flyering",
  },
  tracking: { promoCodes: true, qrCodes: true, placeholders: false },
  orderDetails: { quoteItemLabel: "Flyering" },
  getStepCompletion: getH2hStepCompletion,
  lastActiveStep: flyeringLastActiveStep,
};

registerChannelConfig(h2hChannelConfig);
